import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, IconButton, Link, Stack, Typography } from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import { func, oneOf, shape } from 'prop-types';
import * as R from 'ramda';
import { useUserRolePermissions } from 'hooks';
import AddLocationsSection from './AddLocationsSection';
import LocationsGrid from './LocationsGrid';

const NO_TERMINALS_WARNING =
  'There are no terminals configured for this authorization. At least one terminal is required to enable loading for this customer.';

const SINGLE_TERMINAL_MESSAGE =
  'Changing terminals is not available for Single Terminal authorizations.';

const TerminalsTabContent = ({ customerAuth, mode, setCustomerAuth }) => {
  const { isProductLoadingManage } = useUserRolePermissions();
  const isEditMode = mode === 'edit';
  const isSingleControl = customerAuth.controlType === 'SINGLE';
  // SINGLE authorizations cannot change terminals at all.
  const canManage = isEditMode && isProductLoadingManage && !isSingleControl;
  const [isAddSectionOpen, setIsAddSectionOpen] = useState(false);
  const [newTerminalIds, setNewTerminalIds] = useState([]);
  const previousAuthorizationKeyRef = useRef(null);
  const terminals = useMemo(() => customerAuth.terminals ?? [], [customerAuth.terminals]);
  const authorizationKey = customerAuth?.customerAuthorizationId ?? customerAuth?.customerIdName;
  const configuredGridHeight =
    terminals.length > 10
      ? 'clamp(320px, calc(100vh - 270px), 660px)'
      : 'clamp(240px, calc(100vh - 360px), 520px)';
  const showNoTerminalsWarning =
    isEditMode && R.not(R.isNil(customerAuth.terminals)) && terminals.length === 0;
  const showSingleTerminalMessage = isEditMode && isProductLoadingManage && isSingleControl;

  useEffect(() => {
    const previousAuthorizationKey = previousAuthorizationKeyRef.current;

    if (previousAuthorizationKey && previousAuthorizationKey !== authorizationKey) {
      setIsAddSectionOpen(false);
      setNewTerminalIds([]);
    }

    previousAuthorizationKeyRef.current = authorizationKey;
  }, [authorizationKey]);

  const handleRemoveTerminal = terminalId => {
    setNewTerminalIds(previous => previous.filter(id => id !== terminalId));
    setCustomerAuth(previous => ({
      ...previous,
      terminals: R.reject(R.propEq('terminalId', terminalId), previous.terminals ?? []),
    }));
  };

  const handleAddTerminals = addedTerminals => {
    setNewTerminalIds(previous => [
      ...new Set([...previous, ...addedTerminals.map(R.prop('terminalId'))]),
    ]);
    setCustomerAuth(previous => ({
      ...previous,
      terminals: [...(previous.terminals ?? []), ...addedTerminals],
    }));
  };

  const addLocationButtonSx = {
    borderRadius: 1,
    height: 40,
    minHeight: 40,
    minWidth: 155,
    whiteSpace: 'nowrap',
    ...(isAddSectionOpen
      ? {
          '&:hover': { backgroundColor: 'secondary.dark' },
          backgroundColor: 'secondary.main',
          color: 'common.white',
        }
      : {
          '&:hover': {
            backgroundColor: 'secondary.dark',
            color: 'common.white',
          },
          backgroundColor: 'common.white',
          border: '1px solid',
          borderColor: 'secondary.main',
          color: 'secondary.main',
        }),
  };

  const removeColumn = useMemo(
    () =>
      canManage
        ? [
            {
              align: 'center',
              field: 'remove',
              getApplyQuickFilterFn: undefined,
              headerAlign: 'center',
              headerName: 'Remove',
              renderCell: ({ row }) => (
                <IconButton
                  aria-label={`remove ${row.locationName}`}
                  onClick={() => handleRemoveTerminal(row.id)}
                  size="small"
                >
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              ),
              resizable: false,
              sortable: false,
              width: 90,
            },
          ]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canManage]
  );

  return (
    <Stack spacing={2}>
      {showNoTerminalsWarning && (
        <Alert
          icon={<ErrorRoundedIcon color="error" />}
          severity="error"
          sx={{
            '& .MuiAlert-icon': {
              alignItems: 'center',
              color: 'error.main',
              display: 'flex',
              mr: 1,
              py: 0,
            },
            '& .MuiAlert-message': {
              alignItems: 'center',
              display: 'flex',
              py: 0,
            },
            alignItems: 'center',
            backgroundColor: 'error.light',
            color: 'error.main',
          }}
        >
          {NO_TERMINALS_WARNING}
        </Alert>
      )}
      {canManage && showNoTerminalsWarning && (
        <Stack alignItems="flex-end">
          <Button
            aria-controls="add-locations-section"
            aria-expanded={isAddSectionOpen}
            color="success"
            onClick={() => setIsAddSectionOpen(previous => !previous)}
            startIcon={<AddRoundedIcon />}
            sx={addLocationButtonSx}
            variant={isAddSectionOpen ? 'contained' : 'outlined'}
          >
            Add Location
          </Button>
        </Stack>
      )}
      {!showNoTerminalsWarning && (
        <LocationsGrid
          aboveGridContent={
            <>
              {showSingleTerminalMessage && (
                <Alert
                  icon={<ErrorRoundedIcon color="error" />}
                  severity="error"
                  sx={{
                    '& .MuiAlert-icon': {
                      alignItems: 'center',
                      color: 'error.main',
                      display: 'flex',
                      mr: 1,
                      py: 0,
                    },
                    '& .MuiAlert-message': {
                      alignItems: 'center',
                      display: 'flex',
                      py: 0,
                    },
                    alignItems: 'center',
                    backgroundColor: 'error.light',
                    color: 'error.main',
                    mb: 2,
                  }}
                >
                  {SINGLE_TERMINAL_MESSAGE}
                </Alert>
              )}
              <Typography sx={{ display: 'block', px: 1, py: 0.75 }} variant="caption">
                To change the Loading status for a terminal,{' '}
                <Link
                  href="mailto:atlasshipperservices@magellanlp.com?subject=Terminal Loading Status"
                  sx={{ color: 'secondary.main', textDecoration: 'none' }}
                >
                  click here to email Magellan Support
                </Link>
              </Typography>
            </>
          }
          belowQuickFilterContent={
            canManage ? (
              <AddLocationsSection
                customerIdName={customerAuth.customerIdName}
                existingTerminals={terminals}
                isOpen={isAddSectionOpen}
                onAddTerminals={handleAddTerminals}
                setIsOpen={setIsAddSectionOpen}
              />
            ) : null
          }
          darkHeader
          extraColumns={removeColumn}
          gridHeight={configuredGridHeight}
          hideColumns={[]}
          newTerminalIds={newTerminalIds}
          quickFilterAriaLabel="Filter Locations"
          quickFilterPlaceholder="Filter locations..."
          terminals={terminals}
          topRightContent={
            canManage ? (
              <Button
                aria-controls="add-locations-section"
                aria-expanded={isAddSectionOpen}
                color="success"
                onClick={() => setIsAddSectionOpen(previous => !previous)}
                startIcon={<AddRoundedIcon />}
                sx={addLocationButtonSx}
                variant={isAddSectionOpen ? 'contained' : 'outlined'}
              >
                Add Location
              </Button>
            ) : null
          }
        />
      )}
      {canManage && showNoTerminalsWarning && (
        <AddLocationsSection
          customerIdName={customerAuth.customerIdName}
          existingTerminals={terminals}
          isOpen={isAddSectionOpen}
          onAddTerminals={handleAddTerminals}
          setIsOpen={setIsAddSectionOpen}
        />
      )}
    </Stack>
  );
};

TerminalsTabContent.propTypes = {
  customerAuth: shape({}).isRequired,
  mode: oneOf(['edit', 'view']).isRequired,
  setCustomerAuth: func.isRequired,
};

export default TerminalsTabContent;
